/**
 * ManualRatesEditor — "Today's Rates — manual entry" section of the Mortgage
 * Monitor Admin drawer.
 *
 * One field per market_rates product, pre-filled with the latest live rate
 * (the same field shows freshly fetched values after any fetch). Typing a new
 * value and applying calls the setManualMarketRates callable, which writes
 * rate_date=today rows with source ADMIN_MANUAL — those win everywhere
 * (rate cards, scans, alerts) until the next fetch (daily 6:00 AM ET cron or
 * "Fetch market rates now") deletes them and fetched values return. The host
 * page chains a book re-scan after a successful apply, so opportunities
 * re-score against the new rates without a separate click.
 *
 * Persistent adjustments belong in the settings panel below (spreads and
 * per-type % overrides, re-applied on every fetch).
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { FiEdit3 } from 'react-icons/fi';

const PRODUCTS = [
  { product: '30yr_fixed', label: 'Conventional 30yr' },
  { product: '15yr_fixed', label: '15yr Fixed' },
  { product: 'fha_30yr', label: 'FHA 30yr' },
  { product: 'va_30yr', label: 'VA 30yr' },
  { product: 'usda_30yr', label: 'USDA 30yr' },
  { product: 'nonqm_30yr', label: 'Non-QM 30yr' },
];

const SOURCE_BADGES = {
  FRED_PMMS: { label: 'FRED', color: 'blue' },
  LOANSIFTER: { label: 'LoanSifter', color: 'green' },
  DERIVED_SPREAD: { label: 'derived', color: 'purple' },
  ADMIN_OVERRIDE: { label: 'override', color: 'yellow' },
  ADMIN_MANUAL: { label: 'manual', color: 'orange' },
};

const round3 = (n) => Math.round(Number(n) * 1000) / 1000;
const fmtRate = (r) => round3(r).toFixed(3).replace(/0+$/, '').replace(/\.$/, '');

/**
 * @param {Object} rateTrend getMortgageMonitor rateTrend ({ product: [{ date, rate, source }] }).
 * @param {Function} onApply async ({ product: rate }) => { success, error } — only changed products.
 * @param {boolean} isBusy true while the manual-rate callable is in flight.
 */
export default function ManualRatesEditor({ rateTrend, onApply, isBusy }) {
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const rowBg = useColorModeValue('gray.50', 'gray.700');

  // Latest live value per product (rate cards show the same numbers).
  const serverValues = useMemo(() => {
    const out = {};
    for (const { product } of PRODUCTS) {
      const series = (rateTrend && rateTrend[product]) || [];
      const latest = series.length ? series[series.length - 1] : null;
      out[product] = latest
        ? { rate: Number(latest.rate), source: latest.source || null, date: latest.date }
        : null;
    }
    return out;
  }, [rateTrend]);

  // Fields re-sync to server values whenever the trend reloads — after a
  // fetch, the same fields show the fetched rates again.
  const [values, setValues] = useState({});
  useEffect(() => {
    const next = {};
    for (const { product } of PRODUCTS) {
      const server = serverValues[product];
      next[product] = server ? fmtRate(server.rate) : '';
    }
    setValues(next);
  }, [serverValues]);

  // Only valid values that differ from the live rate get submitted.
  const changes = useMemo(() => {
    const out = {};
    for (const { product } of PRODUCTS) {
      const raw = values[product];
      if (raw === '' || raw === undefined || raw === null) continue;
      const num = Number(raw);
      if (!Number.isFinite(num) || num <= 0 || num > 15) continue;
      const server = serverValues[product];
      if (server && round3(server.rate) === round3(num)) continue;
      out[product] = round3(num);
    }
    return out;
  }, [values, serverValues]);

  const dirty = Object.keys(changes).length > 0;

  const handleApply = async () => {
    if (!dirty) return;
    await onApply(changes);
    // On success the parent reloads the monitor; the useEffect above re-syncs
    // the fields (now showing the manual rates with a "manual" badge).
  };

  return (
    <Box borderWidth="1px" borderColor={cardBorder} borderRadius="md" p={4} data-tour="monitor-admin-manual-rates">
      <HStack spacing={2} mb={1}>
        <Icon as={FiEdit3} color="orange.400" />
        <Heading size="xs">Today&apos;s Rates — manual entry</Heading>
      </HStack>
      <Text fontSize="sm" color={subtleText} mb={3}>
        Type over any rate to set it manually. Applying re-scans the book automatically so opportunities
        re-score against the new rates. Manual rates hold until the next fetch (daily 6:00 AM ET or
        &quot;Fetch market rates now&quot;), which restores fetched values in these same fields. For a rate that
        survives fetches, use the per-type % override below.
      </Text>

      <VStack align="stretch" spacing={2}>
        {PRODUCTS.map(({ product, label }) => {
          const server = serverValues[product];
          const badge = server && server.source ? SOURCE_BADGES[server.source] : null;
          return (
            <HStack key={product} bg={rowBg} borderRadius="md" px={3} py={2} spacing={4} wrap="wrap">
              <Text fontSize="sm" fontWeight="600" w="140px">{label}</Text>
              <NumberInput
                size="sm"
                w="120px"
                value={values[product] ?? ''}
                min={0}
                max={15}
                step={0.125}
                onChange={(str) => setValues((prev) => ({ ...prev, [product]: str }))}
                isDisabled={isBusy}
              >
                <NumberInputField placeholder="—" />
                <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
              </NumberInput>
              {badge ? (
                <Badge colorScheme={badge.color} fontSize="0.7rem">{badge.label}</Badge>
              ) : (
                <Badge colorScheme="gray" fontSize="0.7rem">no data</Badge>
              )}
              {server?.date && (
                <Text fontSize="xs" color={subtleText}>as of {server.date}</Text>
              )}
            </HStack>
          );
        })}
      </VStack>

      <Button
        size="sm"
        mt={3}
        colorScheme="orange"
        variant="outline"
        leftIcon={<FiEdit3 />}
        onClick={handleApply}
        isLoading={isBusy}
        isDisabled={!dirty}
      >
        Apply manual rates
      </Button>
    </Box>
  );
}
